import * as ents from '@attain-apps/entities'
import * as ds from '@attain-apps/fe/web/main/lib/design'
import type cytoscape from 'cytoscape'

import * as models from '../models'

const BADGE_HEIGHT = 18
const BADGE_OFFSET_Y = 6
const BADGE_WIDTH = 36
const BODY_HORIZONTAL_PADDING = 10
const CARD_HEIGHT = 96
const CARD_WIDTH = 192
const BODY_TEXT_WIDTH = CARD_WIDTH - BODY_HORIZONTAL_PADDING * 2
const DEFAULT_CHARACTER_WIDTH_FACTOR = 0.52
const FONT_FAMILY = 'pt-sans-pro, sans-serif'
const HORIZONTAL_PADDING = 20
const LABEL_CENTER_Y = 50
const LABEL_FONT_SIZE = 13
const LABEL_LINE_HEIGHT = 13
const MAX_LINES_PER_FIELD = 2
const NARROW_CHARACTER_WIDTH_FACTOR = 0.32
const NARROW_CHARACTERS = " !'(),-./:;[]fijlrst|"
const SELECTED_BORDER_SHADES = 2
const SUBLABEL_CENTER_Y = 70
const SUBLABEL_FONT_SIZE = 10
const SUBLABEL_LINE_HEIGHT = 10
const TITLE_TEXT_WIDTH = CARD_WIDTH - HORIZONTAL_PADDING * 2
const TITLE_CENTER_Y = 20
const TITLE_DIVIDER_Y = 30
const TITLE_FONT_SIZE = 10
const TITLE_LINE_HEIGHT = 10
const WIDE_CHARACTER_WIDTH_FACTOR = 0.78
const WIDE_CHARACTERS = '&@MWQGm%#'
type EdgeLineStyle = 'solid' | 'dotted' | 'dashed'

const LINE_STYLE_BY_LEADER_TYPE: Record<
  ents.extra.common.LeaderType,
  EdgeLineStyle
> = {
  [ents.extra.common.LeaderType.Manager]: 'solid',
  [ents.extra.common.LeaderType.IndirectManager]: 'dashed',
  [ents.extra.common.LeaderType.Other]: 'dotted',
}

/**
 * Returns the appropriate styles for all elements in Cytoscape, for the given
 * {@link models.Chart}
 *
 * @see {@link cytoscape.StylesheetJson}
 */
export function getStyles({
  chartId,
}: models.Chart.Identifier): cytoscape.StylesheetJson {
  const colorScale =
    models.Chart.Entry.get(chartId) === 'proposed'
      ? ds.color.Scale.SECONDARY
      : ds.color.Scale.PRIMARY
  const defaultBackgroundColor = '#ffffff'
  const defaultBorderColor = ds.color.Scale.SHADOW.lighter
  const edgeColor = ds.color.Scale.SHADOW.lighter
  const edgeColorHighlighted = ds.color.Scale.SHADOW.neutral
  const futureBackgroundColor = '#ffffff'
  const futureBorderColor = ds.color.Scale.SHADOW.lighter
  const changedBackgroundColor = ds.color.Scale.DANGER.lighter
  const changedBorderColor = ds.color.Scale.DANGER.light
  const selectedBorderColor = colorScale.shadeFromMain(SELECTED_BORDER_SHADES)
  const temporaryBackgroundColor = '#ffffff'
  const temporaryBorderColor = ds.color.Scale.SHADOW.lighter

  const styles: cytoscape.StylesheetJson = [
    {
      selector: 'node',
      style: {
        'background-color': defaultBackgroundColor,
        'background-height': `${CARD_HEIGHT}px`,
        'background-image': (cyNode: cytoscape.NodeSingular) =>
          createNodeCardSvg(cyNode),
        'background-image-containment': 'over',
        'background-position-x': '50%',
        'background-position-y': '50%',
        'background-repeat': 'no-repeat',
        'background-width': `${CARD_WIDTH}px`,
        'border-color': defaultBorderColor,
        'border-width': 1,
        height: `${CARD_HEIGHT}px`,
        label: '',
        shape: 'roundrectangle',
        width: `${CARD_WIDTH}px`,
      },
    },
    {
      selector: 'node.has-children.collapsed',
      style: {
        'background-clip': 'none',
        'background-height': [`${BADGE_HEIGHT}px`, `${CARD_HEIGHT}px`],
        'background-image': (cyNode: cytoscape.NodeSingular) =>
          [
            createBadgeSvg(
              (cyNode.data('descendantCount') as number | undefined) ?? 0,
              false,
            ),
            createNodeCardSvg(cyNode),
          ] as string[],
        'background-image-containment': ['over', 'over'],
        'background-offset-y': [`${BADGE_OFFSET_Y}px`, '0px'],
        'background-position-x': ['50%', '50%'],
        'background-position-y': ['100%', '50%'],
        'background-repeat': ['no-repeat', 'no-repeat'],
        'background-width': [`${BADGE_WIDTH}px`, `${CARD_WIDTH}px`],
        'bounds-expansion': `0 0 ${BADGE_HEIGHT + BADGE_OFFSET_Y} 0`,
      },
    },
    {
      selector: 'node.has-children.expanded',
      style: {
        'background-clip': 'none',
        'background-height': [`${BADGE_HEIGHT}px`, `${CARD_HEIGHT}px`],
        'background-image': (cyNode: cytoscape.NodeSingular) =>
          [
            createBadgeSvg(
              (cyNode.data('descendantCount') as number | undefined) ?? 0,
              true,
            ),
            createNodeCardSvg(cyNode),
          ] as string[],
        'background-image-containment': ['over', 'over'],
        'background-offset-y': [`${BADGE_OFFSET_Y}px`, '0px'],
        'background-position-x': ['50%', '50%'],
        'background-position-y': ['100%', '50%'],
        'background-repeat': ['no-repeat', 'no-repeat'],
        'background-width': [`${BADGE_WIDTH}px`, `${CARD_WIDTH}px`],
        'bounds-expansion': `0 0 ${BADGE_HEIGHT + BADGE_OFFSET_Y} 0`,
      },
    },
    {
      selector: 'node.temporary',
      style: {
        'background-color': temporaryBackgroundColor,
        'border-color': temporaryBorderColor,
        'border-style': 'dashed',
      },
    },
    {
      selector: 'node.future',
      style: {
        'background-color': futureBackgroundColor,
        'border-color': futureBorderColor,
      },
    },
    {
      selector: 'node:selected',
      style: {
        'border-color': selectedBorderColor,
        'border-width': '4px',
      },
    },
    {
      selector: 'edge',
      style: {
        'curve-style': 'round-taxi',
        'line-cap': 'round',
        'line-color': edgeColor,
        'line-opacity': 1,
        'overlay-opacity': 0,
        'target-arrow-shape': 'none',
        'taxi-direction': 'downward',
        'taxi-radius': 4,
        'taxi-turn': '20px',
        'taxi-turn-min-distance': '5px',
        width: 0.75,
      },
    },
    {
      selector: 'edge.highlighted',
      style: {
        'line-color': edgeColorHighlighted,
        'line-opacity': 1,
        width: 1,
        'z-index': 999,
      },
    },
  ]
  const leaderTypeLineStyles = (
    Object.entries(LINE_STYLE_BY_LEADER_TYPE) as [
      ents.extra.common.LeaderType,
      EdgeLineStyle,
    ][]
  ).map(([leaderType, lineStyle]) => ({
    selector: `edge[leaderType = "${leaderType}"]`,
    style: {
      'line-style': lineStyle,
    },
  }))
  styles.push(...leaderTypeLineStyles)

  if (models.Chart.Entry.get(chartId) === 'proposed') {
    styles.push(
      {
        selector: 'node',
        style: {
          opacity: 0.5,
        },
      },
      {
        selector: 'node.changed',
        style: {
          'background-color': changedBackgroundColor,
          'border-color': changedBorderColor,
        },
      },
    )
  }

  return styles
}

/** Builds the expand/collapse badge SVG used on nodes with children. */
function createBadgeSvg(count: number, isExpanded: boolean): string {
  const chevron = isExpanded ? '▾' : '▸'
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='${BADGE_WIDTH}' height='${BADGE_HEIGHT}' viewBox='0 0 ${BADGE_WIDTH} ${BADGE_HEIGHT}'>
    <rect x='0.5' y='0.5' width='${BADGE_WIDTH - 1}' height='${BADGE_HEIGHT - 1}' rx='4' ry='4' fill='#ffffff' stroke='${ds.color.Scale.SHADOW.lighter}' stroke-width='1'/>
    <text x='${BADGE_WIDTH / 2}' y='${BADGE_HEIGHT / 2 + 1}' text-anchor='middle' dominant-baseline='middle' font-family='${FONT_FAMILY}' font-size='11' fill='${ds.color.Scale.SHADOW.neutral}'>${count}${chevron}</text>
  </svg>`
  return `data:image/svg+xml,${encodeURIComponent(svg)}`
}

/** Renders the node card SVG used as the Cytoscape background image. */
function createNodeCardSvg(cyNode: cytoscape.NodeSingular): string {
  const { labelColor, mutedColor } = getNodeCardColors(cyNode)
  const dividerColor = ds.color.Scale.SHADOW.lighter
  const labelLines = wrapText(
    String(cyNode.data('label') ?? ''),
    BODY_TEXT_WIDTH,
    LABEL_FONT_SIZE,
    MAX_LINES_PER_FIELD,
  )
  const sublabelLines = wrapText(
    String(cyNode.data('sublabel') ?? ''),
    BODY_TEXT_WIDTH,
    SUBLABEL_FONT_SIZE,
    MAX_LINES_PER_FIELD,
  )
  const titleLines = wrapText(
    String(cyNode.data('title') ?? ''),
    TITLE_TEXT_WIDTH,
    TITLE_FONT_SIZE,
    MAX_LINES_PER_FIELD,
  )
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='${CARD_WIDTH}' height='${CARD_HEIGHT}' viewBox='0 0 ${CARD_WIDTH} ${CARD_HEIGHT}'>
    <line x1='${HORIZONTAL_PADDING}' y1='${TITLE_DIVIDER_Y}' x2='${CARD_WIDTH - HORIZONTAL_PADDING}' y2='${TITLE_DIVIDER_Y}' stroke='${dividerColor}' stroke-width='1'/>
    ${renderTextBlock({
      align: 'center',
      centerY: TITLE_CENTER_Y,
      color: mutedColor,
      fontSize: TITLE_FONT_SIZE,
      lineHeight: TITLE_LINE_HEIGHT,
      lines: titleLines,
    })}
    ${renderTextBlock({
      align: 'left',
      centerY: LABEL_CENTER_Y,
      color: labelColor,
      fontSize: LABEL_FONT_SIZE,
      horizontalPadding: BODY_HORIZONTAL_PADDING,
      lineHeight: LABEL_LINE_HEIGHT,
      lines: labelLines,
    })}
    ${renderTextBlock({
      align: 'left',
      centerY: SUBLABEL_CENTER_Y,
      color: mutedColor,
      fontSize: SUBLABEL_FONT_SIZE,
      horizontalPadding: BODY_HORIZONTAL_PADDING,
      lineHeight: SUBLABEL_LINE_HEIGHT,
      lines: sublabelLines,
    })}
  </svg>`
  return `data:image/svg+xml,${encodeURIComponent(svg)}`
}

/** Escapes freeform node text before embedding it into SVG markup. */
function escapeXml(text: string): string {
  return text
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&apos;')
}

/** Chooses card text colors that match the node's state styling. */
function getNodeCardColors(cyNode: cytoscape.NodeSingular): {
  labelColor: string
  mutedColor: string
} {
  if (cyNode.hasClass('future') || cyNode.hasClass('temporary')) {
    return {
      labelColor: ds.color.Scale.SHADOW.light,
      mutedColor: ds.color.Scale.SHADOW.light,
    }
  }
  return {
    labelColor: ds.color.Scale.SHADOW.darker,
    mutedColor: ds.color.Scale.SHADOW.neutral,
  }
}

/** Renders one centered or left-aligned SVG text block. */
function renderTextBlock({
  align,
  centerY,
  color,
  fontSize,
  horizontalPadding = HORIZONTAL_PADDING,
  lineHeight,
  lines,
}: {
  align: 'center' | 'left'
  centerY: number
  color: string
  fontSize: number
  horizontalPadding?: number
  lineHeight: number
  lines: readonly string[]
}): string {
  if (lines.length === 0) {
    return ''
  }
  const startY = centerY - (lineHeight * (lines.length - 1)) / 2
  const textAnchor = align === 'left' ? 'start' : 'middle'
  const x = align === 'left' ? horizontalPadding : CARD_WIDTH / 2
  const tspans = lines
    .map(
      (line, index) =>
        `<tspan x='${x}' y='${startY + index * lineHeight}'>${escapeXml(line)}</tspan>`,
    )
    .join('')
  return `<text fill='${color}' font-family='${FONT_FAMILY}' font-size='${fontSize}px' font-weight='400' text-anchor='${textAnchor}'>${tspans}</text>`
}

/** Truncates text so the final rendered line fits within the target width. */
function truncateWithEllipsis(
  text: string,
  maxWidth: number,
  fontSize: number,
): string {
  if (estimateTextWidth(text, fontSize) <= maxWidth) {
    return text
  }

  let truncated = text.trimEnd()
  while (truncated.length > 0) {
    const candidate = `${truncated}…`
    if (estimateTextWidth(candidate, fontSize) <= maxWidth) {
      return candidate
    }
    truncated = truncated.slice(0, -1).trimEnd()
  }

  return '…'
}

/** Wraps node labels into a bounded number of SVG text lines. */
function wrapText(
  text: string,
  maxWidth: number,
  fontSize: number,
  maxLines: number,
): readonly string[] {
  const normalized = text.trim().replace(/\s+/g, ' ')
  if (!normalized) {
    return []
  }

  const lines: string[] = []
  const words = normalized.split(' ')
  let currentLine = ''

  while (words.length > 0) {
    const word = words.shift()
    if (!word) {
      continue
    }
    const candidate = currentLine ? `${currentLine} ${word}` : word

    if (estimateTextWidth(candidate, fontSize) <= maxWidth) {
      currentLine = candidate
      continue
    }

    if (lines.length === maxLines - 1) {
      const remainder = [currentLine, word, ...words].filter(Boolean).join(' ')
      lines.push(truncateWithEllipsis(remainder, maxWidth, fontSize))
      return lines
    }

    if (currentLine) {
      lines.push(currentLine)
      currentLine = word
      continue
    }

    lines.push(truncateWithEllipsis(word, maxWidth, fontSize))
  }

  if (currentLine) {
    lines.push(currentLine)
  }

  return lines.slice(0, maxLines)
}

/** Estimates rendered text width for the fixed SVG font used by node cards. */
function estimateTextWidth(text: string, fontSize: number): number {
  return Array.from(text).reduce((width, character) => {
    if (NARROW_CHARACTERS.includes(character)) {
      return width + fontSize * NARROW_CHARACTER_WIDTH_FACTOR
    }
    if (WIDE_CHARACTERS.includes(character)) {
      return width + fontSize * WIDE_CHARACTER_WIDTH_FACTOR
    }
    return width + fontSize * DEFAULT_CHARACTER_WIDTH_FACTOR
  }, 0)
}
